import React, { FC, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentEmployee,
  getCurrentEmployeeEditProfile,
  getEmployeeAction
} from '../../../../shared/services/employee-service';
import TableRowNoData from '../../../../shared/components/molecules/commons/table-row-no-data';
import { ChildModel } from '../../../../shared/models/employee.model';
import {
  getFixValue,
  handleEmployeeSectionInputChange,
  handleEmployeeSectionInputSelectChange,
  handleEmployeeSectionRemoveByIndex
} from '../../../../shared/utils/function-state';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import DateInput from '../../../../shared/components/atoms/date-input';
import { convertDateToString } from '../../../../shared/utils/functions-date-time';
import { GenderOptions } from '../../../../shared/utils/constants';
import InputSelect from '../../../../shared/components/atoms/input-select';
import { calculateAge } from '../../../../shared/utils/function-employees';
import SectionFileForm from '../section-file-form';
import DeleteConfirmationModal from '../../../../shared/components/molecules/modals/delete-confirmation-modal';
import TableHeaderOrdering, { Header, OrderData } from '../../../../shared/components/table-header-ordering';
import { SortingField } from '../../../../shared/models/common.model';
import { SortingByField } from '../../../../shared/utils/sorting';

interface ChildModelProp extends ChildModel {
  index: number;
}

const ChildForm: FC = () => {
  const employeeAction = useSelector(getEmployeeAction);
  const employeeStateTarget = employeeAction.type === 'view-edit-request' ? getCurrentEmployeeEditProfile : getCurrentEmployee;
  const employee = useSelector(employeeStateTarget);
  const showOrder = employeeAction.type === 'view' || employeeAction.type === 'view-edit-request';
  const [sorting, setSorting] = useState<SortingField>({
    field: null,
    order: null
  });

  const tableRowNoData = () => {
    if (employee.children.length === 0) {
      return <TableRowNoData colspan={7}/>;
    }
    return null;
  };

  const headers: Header[] = [
    {
      label: 'ชื่อ',
      field: 'first_name',
      order: null,
      isOrder: showOrder,
    },
    {
      label: 'นามสกุล',
      field: 'last_name',
      order: null,
      isOrder: showOrder,
    },
    {
      label: 'เพศ',
      field: 'gender',
      order: null,
      isOrder: showOrder,
    },
    {
      label: 'วันเดือนปีเกิด',
      field: 'birthday',
      order: null,
      isOrder: showOrder,
    },
    {
      label: 'อายุ',
      field: 'file',
      order: null,
      isOrder: false,
    },
    {
      label: 'แนบไฟล์',
      field: 'file',
      order: null,
      isOrder: false,
    },
    {
      label: '',
      field: '',
      order: null,
      isOrder: false,
    }
  ];

  const onChangeOrder = (orderData: OrderData) => {
    setSorting({
      field: orderData.field,
      order: orderData.order
    });
  };

  const dataOrder = () => {
    if (showOrder)
      return employee.children.map(data => data).sort((a, b) => {
        return SortingByField(a, b, sorting);
      });
    return employee.children;
  };

  return (
    <div className="h-100 overflow-x">
      <Table bordered>
        <thead>
        <TableHeaderOrdering headers={headers} onChangeOrder={onChangeOrder} isShow={showOrder}/>
        </thead>
        <tbody>
        {
          dataOrder().map((data, index) => {
            return <ChildRow key={index}
                             index={index} {...data}
            />;
          })
        }
        {tableRowNoData()}
        </tbody>
      </Table>
    </div>
  );
};

const ChildRow: FC<ChildModelProp> = ({
                                        index,
                                        first_name,
                                        last_name,
                                        gender,
                                        birthday,
                                        file,
                                        from_id,
                                      }) => {
  const dispatch = useDispatch();
  const employeeAction = useSelector(getEmployeeAction);
  const disable = employeeAction.type === 'view' || employeeAction.type === 'view-edit-request';
  const type = 'children';
  const employeeStateTarget = employeeAction.type === 'view-edit-request' ? getCurrentEmployeeEditProfile : getCurrentEmployee;
  const employee = useSelector(employeeStateTarget);
  const oldEmployee = useSelector(getCurrentEmployee);
  let fromEmployee: any | null = null;

  if (from_id) {
    const found = oldEmployee[type].find(data => data.id === from_id);
    if (found)
      fromEmployee = found;
  }

  const validateEditRequest = (value: any, key: keyof ChildModel) => {
    if (employeeAction.type === 'view-edit-request') {
      if (fromEmployee && from_id) {
        if (value !== fromEmployee[key])
          return 'field-warning';
      }
      if (!from_id) {
        return 'field-new';
      }
    }
    return '';
  };

  const [showModal, setShowModal] = useState(false);

  const deleteButton = () => {
    if (!disable) {
      return <FontAwesomeIcon icon={faCircleXmark} className="text-danger pointer" onClick={handleDelete}/>;
    }
    return null;
  };

  const handleDelete = () => {
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    handleEmployeeSectionRemoveByIndex(type, index, dispatch);
    setShowModal(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const getAge = () => {
    if (birthday) {
      return `${calculateAge(birthday)} ปี`;
    }
    return 'กรุณาเลือกวันเดือนปีเกิด';
  };

  return (
    <tr className="bg-white">
      <td className={`${validateEditRequest(first_name, 'first_name')}`}>
        <input type="text" className="form-control" name="first_name" disabled={disable}
               placeholder="" value={first_name || ''} required={true}
               onChange={(e) => handleEmployeeSectionInputChange(type, index, e, dispatch)}/>
      </td>
      <td className={`${validateEditRequest(last_name, 'last_name')}`}>
        <input type="text" className="form-control" name="last_name" disabled={disable}
               placeholder="" value={last_name || ''} required={true}
               onChange={(e) => handleEmployeeSectionInputChange(type, index, e, dispatch)}/>
      </td>
      <td className={`${validateEditRequest(gender, 'gender')}`}>
        <InputSelect options={GenderOptions} placeholder="กรุณาเลือก" name="gender"
                     onChange={e => handleEmployeeSectionInputSelectChange(type, index, e, dispatch)}
                     value={gender}
                     isClearable={false} isDisable={disable}/>
      </td>
      <td className={`${validateEditRequest(birthday, 'birthday')}`}>
        <DateInput date={birthday ? new Date(birthday) : null} required={true}
                   positionAbsolute={false}
                   onChange={
                     (value: Date | null) => handleEmployeeSectionInputChange(
                       type,
                       index,
                       getFixValue('birthday', value ? convertDateToString(value) : null),
                       dispatch
                     )
                   }
                   maxDate={new Date()}
                   disable={disable}/>
      </td>
      <td>
        <input type="text" className="form-control" name="age" disabled={true}
               placeholder="" readOnly={true} value={getAge()}/>
      </td>
      <td className={`${validateEditRequest(file, 'file')}`}>
        <SectionFileForm section_key={'children'} index={
          disable ? employee.children.findIndex(data =>
            data.first_name === first_name &&
            data.last_name === last_name &&
            data.gender === gender &&
            data.birthday === birthday
          ) : index
        }/>
      </td>
      <td className="text-center">
        {deleteButton()}
      </td>
      <DeleteConfirmationModal
        show={showModal}
        title={'ยืนยันการลบข้อมูล'}
        description={'คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลนี้'}
        onHide={handleCloseModal}
        onConfirm={handleConfirmDelete}
      />
    </tr>
  );
};

export default ChildForm;